import { NotificationCard } from '../../components/Card';
import { Flex, Icon, Center, Button } from '@chakra-ui/react';
import { LayersThree01 } from '@kula-ai/icons/dual-tone/LayersThree01';
import { NotificationTime } from '../../components/Time';
import {
  RequisitionDetailsSection,
  RequisitionDetailsList,
  useRequisitionData,
} from './components';
import { KText } from '@/ui-library/components';
import { ModalWithForm } from '@/components/ats';
import { useRequisitionApproval } from '@/pages/requisitions/hooks';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';
import { XCircle } from '@kula-ai/icons/dual-tone/XCircle';

const RequisitionApprovalRequest = ({
  notification = {},
  onClose = () => {},
}) => {
  const {
    requisition_id,
    role_name,
    requester_name,
    head_count,
    buildRequisitionDetails,
    reapproval_note,
    approval_status,
    requisition_ids,
  } = useRequisitionData(notification);

  const {
    actionStatus,
    isApproveActionLoading,
    rejectModalActions,
    rejectApproveFields,
    handleApprove,
    handleReject,
    handleRejectSubmit,
  } = useRequisitionApproval({
    requisitionId: requisition_id,
    requisitionIds: requisition_ids,
  });

  const detailsData = buildRequisitionDetails([
    { label: 'Requested by', value: requester_name },
  ]);

  return (
    <>
      <Flex alignItems="baseline" gap="6px">
        <Center minW="34px" h="34px" borderRadius="sm" bg="pastel-2-light">
          <Icon as={LayersThree01} color="pastel-2-dark" width="16px" />
        </Center>
        <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
          {requisition_ids?.length ? (
            <KText>
              <KText as="span" fontWeight="semiBold">
                {requester_name}
              </KText>{' '}
              requests approval for{' '}
              <KText as="span" fontWeight="semiBold">
                {head_count}
              </KText>{' '}
              requisitions for{' '}
              <KText as="span" fontWeight="semiBold">
                {role_name}
              </KText>
            </KText>
          ) : (
            <KText>
              <KText as="span" fontWeight="semiBold">
                {requester_name}
              </KText>{' '}
              requests requisition approval for{' '}
              <KText as="span" fontWeight="semiBold">
                {role_name}
              </KText>
            </KText>
          )}

          {reapproval_note && (
            <RequisitionDetailsSection
              title="Note to approvers"
              isCollapsible={false}
            >
              <Flex flexDir="column" gap="16px" p="16px" bg="white">
                <KText size="sm-medium" whiteSpace="pre-wrap">
                  {reapproval_note}
                </KText>
              </Flex>
            </RequisitionDetailsSection>
          )}

          <RequisitionDetailsSection>
            <RequisitionDetailsList data={detailsData} />
          </RequisitionDetailsSection>

          <Flex flexDir="row" gap="8px">
            {actionStatus === 'approved' || approval_status === 'approved' ? (
              <Flex alignItems="center" gap="4px">
                <Icon as={CheckCircle} color="pastel-2-dark" width="16px" />
                <KText color="pastel-2-dark" size="sm-medium">
                  Approved
                </KText>
              </Flex>
            ) : actionStatus === 'rejected' ||
              approval_status === 'rejected' ? (
              <Flex alignItems="center" gap="4px">
                <Icon as={XCircle} color="pastel-1-dark" width="16px" />
                <KText color="pastel-1-dark" size="sm-medium">
                  Rejected
                </KText>
              </Flex>
            ) : (
              <>
                <Button
                  variant="primary"
                  onClick={handleApprove}
                  size="sm"
                  isLoading={isApproveActionLoading}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  onClick={handleReject}
                  size="sm"
                  isLoading={isApproveActionLoading}
                >
                  Reject
                </Button>
              </>
            )}
          </Flex>

          <NotificationTime notification={notification} />
        </Flex>
      </Flex>

      {/* Reject Modal */}
      {rejectModalActions.isOpen && (
        <ModalWithForm
          isOpen={rejectModalActions.isOpen}
          onClose={() => rejectModalActions.onClose()}
          headerText="Reason for rejection?"
          subHeaderText="Provide detailed feedback on why you are rejecting this requisition"
          primaryCtaText="Reject"
          formConfig={rejectApproveFields}
          handleSubmit={handleRejectSubmit}
          isCtaLoading={isApproveActionLoading}
          autoFocus={false}
          formContainerStyles={{
            m: '-10px 0',
          }}
          skipDirtyCheck={true}
        />
      )}
    </>
  );
};

export const RequisitionApprovalRequestNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <RequisitionApprovalRequest
        notification={notification}
        onClose={onClose}
      />
    </NotificationCard>
  );
};
